import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Inbox, LogOut, Plus, Shield } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspaces } from "@/contexts/WorkspaceContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CreateWorkspaceDialog from "@/components/CreateWorkspaceDialog";

const WS_PALETTE = [
  "#D96C4A",
  "#3A5A40",
  "#5B4C7A",
  "#2C5F77",
  "#7A4A2C",
  "#8C3A1F",
  "#4B6C3A",
  "#7A3D5D",
];

function workspaceInitials(name) {
  const trimmed = (name || "").trim();
  if (!trimmed) return "?";
  const words = trimmed.split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function pillBg(name) {
  let h = 0;
  for (const c of name || "") h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return WS_PALETTE[h % WS_PALETTE.length];
}

function userAvatarBg(name) {
  if (name === "Arthur") return "#D96C4A";
  if (name === "Simon") return "#3A5A40";
  return pillBg(name || "?");
}

export default function WorkspaceRail({ activeWorkspaceId }) {
  const { user, logout } = useAuth();
  const { workspaces } = useWorkspaces();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCreate, setShowCreate] = useState(false);

  const isAllChats = location.pathname.startsWith("/all-chats");

  return (
    <>
      <aside
        className="w-16 flex flex-col items-center justify-between bg-[#1A1918] py-3 border-r border-[#2C2A29] flex-shrink-0"
        data-testid="workspace-rail"
      >
        <div className="flex flex-col items-center gap-2 w-full">
          {/* All chats — aggregated cross-workspace view */}
          <button
            onClick={() => navigate("/all-chats")}
            data-testid="rail-all-chats-button"
            title="All my chats"
            aria-label="All my chats"
            className={`relative w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
              isAllChats
                ? "bg-[#D96C4A] text-white shadow-lg"
                : "bg-[#2C2A29] text-[#A5A29E] hover:bg-[#3a3735] hover:text-white"
            }`}
          >
            <Inbox size={16} strokeWidth={2} />
            {isAllChats ? (
              <span className="absolute -left-3 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-[#D96C4A]" />
            ) : null}
          </button>

          {workspaces.length > 0 ? (
            <div className="w-8 h-px bg-[#2C2A29] my-1" aria-hidden="true" />
          ) : null}

          {workspaces.map((ws) => {
            const isActive = ws.id === activeWorkspaceId;
            return (
              <button
                key={ws.id}
                onClick={() => navigate(`/w/${ws.slug}`)}
                data-testid={`rail-workspace-${ws.slug}`}
                title={ws.name}
                aria-label={`Open ${ws.name}`}
                aria-current={isActive ? "page" : undefined}
                className={`relative w-10 h-10 rounded-lg flex items-center justify-center font-sans font-semibold text-sm transition-all duration-150 ${
                  isActive
                    ? "ring-2 ring-white scale-105 shadow-lg"
                    : "opacity-80 hover:opacity-100 hover:scale-105"
                }`}
                style={{ background: pillBg(ws.name), color: "white" }}
              >
                {workspaceInitials(ws.name)}
                {isActive ? (
                  <span className="absolute -left-3 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-white" />
                ) : null}
              </button>
            );
          })}

          {/* Create workspace */}
          <button
            onClick={() => setShowCreate(true)}
            data-testid="rail-create-workspace-button"
            title="Create a workspace"
            aria-label="Create a workspace"
            className="w-10 h-10 rounded-lg flex items-center justify-center bg-transparent text-[#A5A29E] hover:bg-[#2C2A29] hover:text-white transition-all border border-dashed border-[#3a3735]"
          >
            <Plus size={16} strokeWidth={2.2} />
          </button>
        </div>

        {/* User avatar — bottom, with logout dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="rail-user-menu-button"
              aria-label={`Account: ${user?.display_name || ""}`}
              className="w-10 h-10 rounded-full flex items-center justify-center font-serif text-base text-white hover:scale-105 transition-transform shadow-md ring-1 ring-[#2C2A29]"
              style={{ background: userAvatarBg(user?.display_name) }}
              title={user?.display_name}
            >
              {user?.display_name?.[0] || "?"}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="right"
            align="end"
            className="ml-2 min-w-[220px]"
            data-testid="rail-user-menu"
          >
            <div className="px-3 py-2.5">
              <div
                className="font-sans text-sm text-[#2C2A29] font-semibold truncate"
                data-testid="rail-user-menu-name"
              >
                {user?.display_name}
              </div>
              <div className="font-sans text-xs text-[#7A7571] truncate">
                {user?.email}
              </div>
            </div>
            <DropdownMenuSeparator />
            {user?.is_superadmin ? (
              <DropdownMenuItem
                onClick={() => navigate("/superadmin")}
                data-testid="rail-user-menu-superadmin"
                className="text-[#D96C4A] focus:text-[#D96C4A] cursor-pointer"
              >
                <Shield size={14} className="mr-2" /> Superadmin
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              onClick={logout}
              data-testid="rail-user-menu-logout"
              className="text-[#8C3A1F] focus:text-[#8C3A1F] cursor-pointer"
            >
              <LogOut size={14} className="mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </aside>

      <CreateWorkspaceDialog open={showCreate} onOpenChange={setShowCreate} />
    </>
  );
}
